import {Formik} from "formik";
import PropTypes from "prop-types";
import React from "react";
import {Button, Form, FormFeedback, FormGroup, Input, Label} from "reactstrap";
import {Translate, withLocalize} from "react-localize-redux";

import {isEmptyString, isGroupValid} from "../../../../utils/string-utils";
import {DEJITTER_BUFFER_MAX, DEJITTER_BUFFER_MIN, DEJITTER_BUFFER_DEFAULT, SDI} from "../../../../constants";
import AWConfirm from "@aviwest/ui-kit/dist/js/components/confirm";
import HelpLayout from "../../../common/help-layout";

const propTypes = {
  config: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    sdiIdx: PropTypes.number.isRequired,
    sdiNbChannels: PropTypes.number.isRequired
  }),
  forbiddenNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  alert: PropTypes.node,
  protocol: PropTypes.node
};

const SDIForm = (props) => {
  const { config, forbiddenNames, translate, alert, protocol } = props;

  const handleFormSubmit = (values) => {

    props.onSubmit(values);
  };

  const handleValidation = (values) => {
    const errors = {};

    // Name
    if(isEmptyString(values.name)){
      errors.name = 'genericLabel.REQUIRED_FIELD.text';
    }
    else if(values.name.length >= 32){
      errors.name = 'genericLabel.TOO_LONG.text';
    }
    else if(forbiddenNames.indexOf(values.name) !== -1){
      errors.name = 'genericLabel.DUPLICATED_VALUES.text';
    }
    else if(!isGroupValid(values.name)){
      errors.name = 'genericLabel.INVALID_FORMAT.text'
    }

    if(isEmptyString(values.sdiIdx)){
      errors.sdiIdx = 'genericLabel.REQUIRED_FIELD.text';
    }

    if(isEmptyString(values.sdiNbChannels)){
      errors.sdiNbChannels = 'genericLabel.REQUIRED_FIELD.text';
    }

    // DeJitterBuffer
    if(isEmptyString(values.dejitterBuffer)){
      errors.dejitterBuffer = 'genericLabel.REQUIRED_FIELD.text';
    }
    else if(parseInt(values.dejitterBuffer) < DEJITTER_BUFFER_MIN || parseInt(values.dejitterBuffer) > DEJITTER_BUFFER_MAX){
      errors.dejitterBuffer = 'genericLabel.INVALID_FORMAT.text';
    }

    return errors;
  };

  return (
    <Formik initialValues={{
              id: config ? config.id : undefined,
              type: SDI,
              name: config ? config.name : '',
              sdiIdx: config ? config.sdiIdx : '',
              sdiNbChannels: config ? config.sdiNbChannels : '',
              dejitterBuffer: config ? config.dejitterBuffer : DEJITTER_BUFFER_DEFAULT
            }}
            validate={ handleValidation }
            validateOnBlur={false}
            validateOnChange={true}
            onSubmit={ handleFormSubmit }>
      {({
          values,
          errors,
          dirty,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          /* and other goodies */
        }) => (
        <Form onSubmit={ handleSubmit }>

          <HelpLayout
          filename={`ref_input_profile_SDI.html`}
          form={<fieldset disabled={config && config.enable === true}>
            {alert}
            {protocol}

            <FormGroup>
              <Label for="name">
                <Translate id="genericLabel.NAME.text"/>
              </Label>
              <Input type="text"
                    name="name"
                    id="settings_sdi_name"
                    invalid={errors.name !== undefined}
                    placeholder={ translate('genericLabel.NAME.text') }
                    value={values.name}
                    onChange={handleChange}/>
              <FormFeedback>
                <Translate id={errors.name} />
              </FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label for="sdiIdx">
                <Translate id="genericLabel.SDI_IDX.text"/>
              </Label>
              <Input type="number"
                    name="sdiIdx"
                    id="settings_sdi_sdiIdx"
                    invalid={errors.sdiIdx !== undefined}
                    placeholder={ translate('genericLabel.SDI_IDX.text') }
                    value={values.sdiIdx}
                    onChange={handleChange}/>
              <FormFeedback>
                <Translate id={errors.sdiIdx} />
              </FormFeedback>
            </FormGroup>
            <FormGroup>
              <Label for="sdiNbChannels">
                <Translate id="genericLabel.SDI_NB_CHANNELS.text"/>
              </Label>
              <Input type="number"
                    name="sdiNbChannels"
                    id="settings_sdi_sdiNbChannels"
                    invalid={errors.sdiNbChannels !== undefined}
                    placeholder={ translate('genericLabel.SDI_NB_CHANNELS.text') }
                    value={values.sdiNbChannels}
                    onChange={handleChange}/>
              <FormFeedback>
                <Translate id={errors.sdiNbChannels} />
              </FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label for="dejitterBuffer">
                <Translate id="genericLabel.DEJITTER_BUFFER.text"/>
              </Label>
              <Input type="number"
                    name="dejitterBuffer"
                    id="settings_sdi_dejitterBuffer"
                    invalid={errors.dejitterBuffer !== undefined}
                    min={DEJITTER_BUFFER_MIN}
                    max={DEJITTER_BUFFER_MAX}
                    placeholder={ translate('genericLabel.DEJITTER_BUFFER.text') }
                    value={values.dejitterBuffer}
                    onChange={handleChange}/>
              <FormFeedback>
                <Translate id={errors.dejitterBuffer} />
              </FormFeedback>
              <div className="indicator">
                <Translate id={`genericLabel.DEJITTER_BUFFER_HELP.text`}/>
              </div>
            </FormGroup>
            </fieldset>}

            buttons={<FormGroup className="buttons">
              { props.onDelete && !props.backToDashboard &&
              <AWConfirm
                onConfirm={props.onDelete}
                confirmationText={ props.translate("genericLabel.SECOND_CLICK_CONFIRM.text") }
                render={(ref, onClick) => (
                  <Button id="settings_sdi_deleteButton"
                  innerRef={ref}
                  onClick={onClick}
                  disabled={config && config.enable === true}
                  className="mr-auto"
                  outline
                  color="primary">
                    <Translate id="genericLabel.DELETE.text"/>
                  </Button>
                )} />
              }
              { props.onCancel &&
              <Button id="settings_sdi_cancelButton"
                onClick={props.onCancel}
                outline
                color="primary">
                <Translate id="genericLabel.CANCEL.text"/>
              </Button>
              }
              <Button id="settings_sdi_saveButton"
                      color="primary"
                      disabled={!dirty}
                      type="submit">
                <Translate id="genericLabel.SAVE.text"/>
              </Button>
            </FormGroup>
          } />
        </Form>
      )}
    </Formik>
  );
};

SDIForm.propTypes = propTypes;

export default withLocalize(SDIForm);